import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Shield, Heart, Users, Star, ChevronLeft, ChevronRight, CheckCircle, FileText, ArrowRight, Phone, TrendingUp, Eye, Smile, Landmark } from 'lucide-react';
import { testimonials, insuranceTypes, companyInfo } from '../utils/mockData';
import { useScrollAnimation, useCountUp } from '../utils/animations';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const coverageOptions = ["Life Insurance","Final Expense","Health & Supplemental","Medicare","IUL","Annuity","Dental","Vision","Not Sure / Need Guidance"];

const QuoteForm = () => {
  const [data, setData] = useState({ fullName:'', phone:'', email:'', zipCode:'', coverageType:'', bestTime:'' });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState('');
  const submit = async (e) => { e.preventDefault(); if(!data.coverageType){ setErr('Please select a coverage type'); return; } setErr(''); setSubmitting(true); try { const r = await fetch(`${API_URL}/api/quote`,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(data)}); const j = await r.json(); if(j.success) setDone(true); } catch { /* ignore */ } setSubmitting(false); };
  if(done) return <div className="text-center py-6" data-testid="quote-form-success"><div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3"><svg className="w-7 h-7 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"/></svg></div><h3 className="text-xl font-bold text-brand-blue mb-2">Thank You!</h3><p className="text-gray-600 text-sm">Purposefully Covered Insurance Group received your request. A licensed representative will contact you soon.</p></div>;
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="quote-form">
      <div><input type="text" required value={data.fullName} onChange={e=>setData({...data,fullName:e.target.value})} className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-blue" placeholder="Full Name *" data-testid="quote-fullname"/></div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <input type="tel" required value={data.phone} onChange={e=>setData({...data,phone:e.target.value})} className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-blue" placeholder="Phone Number *" data-testid="quote-phone"/>
        <input type="email" required value={data.email} onChange={e=>setData({...data,email:e.target.value})} className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-blue" placeholder="Email Address *" data-testid="quote-email"/>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <input type="text" required value={data.zipCode} onChange={e=>setData({...data,zipCode:e.target.value})} className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-blue" placeholder="ZIP Code *" data-testid="quote-zipcode"/>
        <Select value={data.coverageType} onValueChange={v=>{setData({...data,coverageType:v}); setErr('');}}>
          <SelectTrigger className="w-full h-auto px-4 py-3 border border-gray-300 rounded-md bg-white text-base text-gray-900 data-[placeholder]:text-gray-500 focus:ring-2 focus:ring-brand-blue" data-testid="quote-coverage-type"><SelectValue placeholder="Coverage Type *" /></SelectTrigger>
          <SelectContent>{coverageOptions.map(o=><SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {err && <p className="text-sm text-red-600" data-testid="quote-coverage-error">{err}</p>}
      <Select value={data.bestTime} onValueChange={v=>setData({...data,bestTime:v})}>
        <SelectTrigger className="w-full h-auto px-4 py-3 border border-gray-300 rounded-md bg-white text-base text-gray-900 data-[placeholder]:text-gray-500 focus:ring-2 focus:ring-brand-blue" data-testid="quote-best-time"><SelectValue placeholder="Best Time to Contact" /></SelectTrigger>
        <SelectContent><SelectItem value="Morning">Morning</SelectItem><SelectItem value="Afternoon">Afternoon</SelectItem><SelectItem value="Evening">Evening</SelectItem></SelectContent>
      </Select>
      <button type="submit" disabled={submitting} className="w-full bg-brand-orange text-white px-6 py-4 rounded-md hover:bg-brand-blue transition font-semibold text-lg disabled:opacity-50" data-testid="quote-submit-btn">{submitting?'Submitting...':'Get My Free Quote'}</button>
      <p className="text-center text-xs text-gray-500">Your information is secure and confidential</p>
    </form>
  );
};

const AnimatedSection = ({ children, animation = 'animate-fade-up', className = '', delay = '' }) => {
  const [ref, isVisible] = useScrollAnimation();
  return (
    <div ref={ref} className={`${animation} ${isVisible ? 'visible' : ''} ${className}`} style={delay ? { transitionDelay: delay } : {}}>
      {children}
    </div>
  );
};

const iconMap = {
  shield: Shield,
  umbrella: Shield,
  heart: Heart,
  plus: Heart,
  trending: TrendingUp,
  landmark: Landmark,
  smile: Smile,
  eye: Eye,
};

const Home = () => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  const nextTestimonial = () => setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  const prevTestimonial = () => setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);

  return (
    <div className="min-h-screen">
      {/* 1. Hero Section */}
      <section
        className="relative bg-cover bg-center py-12 md:py-14 overflow-hidden"
        style={{
          backgroundImage: 'linear-gradient(135deg, rgba(0, 55, 140, 0.93), rgba(0, 71, 171, 0.87), rgba(65, 105, 225, 0.82)), url(https://images.unsplash.com/photo-1511895426328-dc8714191300?w=1200)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
        data-testid="hero-section"
      >
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/3"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-brand-orange/10 rounded-full translate-y-1/2 -translate-x-1/4"></div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <span className="inline-block text-brand-orange font-semibold tracking-widest uppercase text-sm mb-4" data-testid="hero-eyebrow">
                Coverage With Purpose
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                Protect Your Family Without Overpaying For Coverage
              </h1>
              <p className="text-base md:text-lg mb-8 leading-relaxed text-white/90">
                Purposefully Covered Insurance Group helps individuals and families find coverage with purpose. Compare Life Insurance, Final Expense, Health Coverage, Medicare, IULs, Annuities, Dental, and Vision options from trusted insurance carriers.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <a
                  href="#quote"
                  className="bg-brand-orange text-white px-8 py-4 rounded-md hover:bg-brand-orange-light transition font-semibold text-center text-lg shadow-lg hover:shadow-xl hover:-translate-y-0.5 transform"
                  data-testid="hero-cta-quote"
                >
                  Get My Free Quote
                </a>
                <a
                  href={`tel:${companyInfo.phoneRaw}`}
                  className="bg-white/10 backdrop-blur-sm border border-white/30 text-white px-8 py-4 rounded-md hover:bg-white/20 transition font-semibold text-center text-lg flex items-center justify-center gap-2"
                  data-testid="hero-cta-call"
                >
                  <Phone size={20} />
                  Call {companyInfo.phone}
                </a>
              </div>
              <div className="flex items-center gap-6 text-sm text-white/80">
                <div className="flex items-center gap-1">
                  <div className="flex">
                    {[...Array(5)].map((_, i) => <Star key={i} size={14} className="text-yellow-400 fill-yellow-400" />)}
                  </div>
                  <span className="ml-1">4.9/5 Rating</span>
                </div>
                <div className="flex items-center gap-1">
                  <Shield size={14} className="text-brand-orange" />
                  <span>Licensed & Insured</span>
                </div>
              </div>
            </div>

            {/* David Ashley Personal Trust Section */}
            <div className="bg-white rounded-xl shadow-2xl p-8">
              <div className="text-center">
                <img
                  src="https://customer-assets.emergentagent.com/job_jovial-saha-5/artifacts/jmtbnh10__DSA0690BSC.jpg"
                  alt="David Ashley - Purposefully Covered Insurance Group"
                  className="w-28 h-28 rounded-full mx-auto mb-4 object-cover object-top ring-4 ring-brand-blue/10"
                />
                <h3 className="text-xl font-bold text-brand-blue mb-2">Hi, I&apos;m David Ashley</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  I help individuals and families protect what matters most with coverage built around their needs, budget, and goals. At Purposefully Covered Insurance Group, we believe in coverage with purpose. Honest, no-pressure guidance every step of the way.
                </p>
                <a
                  href={`tel:${companyInfo.phoneRaw}`}
                  className="inline-flex items-center gap-2 text-brand-orange font-semibold hover:text-brand-blue transition"
                >
                  <Phone size={18} /> {companyInfo.phone}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. Trust Bar */}
      <section className="bg-white py-5 border-b" data-testid="trust-bar">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
            {[
              { icon: Shield, label: "Licensed Insurance Guidance" },
              { icon: Users, label: "Access to Multiple Carriers" },
              { icon: CheckCircle, label: "Free Coverage Reviews" },
              { icon: Heart, label: "Personalized Recommendations" },
              { icon: Star, label: "Dedicated Local Support" },
            ].map(({ icon: Icon, label }) => (
              <div key={label} className="flex flex-col items-center text-center gap-2">
                <Icon size={24} className="text-brand-orange" />
                <span className="text-sm font-medium text-gray-700">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 3. Coverage Options */}
      <section className="py-8">
        <div className="container mx-auto px-4">
          <AnimatedSection className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-brand-blue mb-4">Our Coverage Solutions</h2>
            <p className="text-base md:text-lg text-gray-600">Coverage with purpose. Options designed to protect what matters most.</p>
          </AnimatedSection>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {insuranceTypes.map((type, idx) => {
              const Icon = iconMap[type.icon] || Shield;
              return (
                <AnimatedSection key={type.id} animation="animate-fade-up" delay={`${idx * 0.08}s`}>
                  <div className="bg-white rounded-xl shadow-md p-6 border-t-4 border-brand-orange hover-lift h-full flex flex-col">
                    <div className="bg-brand-blue text-white w-14 h-14 rounded-full flex items-center justify-center mb-4 mx-auto">
                      <Icon size={28} />
                    </div>
                    <h3 className="text-lg font-bold text-brand-blue mb-2 text-center">{type.title}</h3>
                    <p className="text-gray-600 text-sm text-center mb-4 flex-1">{type.description}</p>
                    <div className="flex flex-col gap-2">
                      <Link
                        to={type.link}
                        className="flex items-center justify-center gap-1 text-brand-orange hover:text-brand-blue font-semibold transition text-sm group"
                        data-testid={`coverage-learn-${type.id}`}
                      >
                        Learn More <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                      </Link>
                    </div>
                  </div>
                </AnimatedSection>
              );
            })}
          </div>
        </div>
      </section>

      {/* 4. How It Works */}
      <section className="py-8 bg-gray-50" data-testid="how-it-works">
        <div className="container mx-auto px-4">
          <AnimatedSection className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-brand-blue mb-4">How It Works</h2>
            <p className="text-base md:text-lg text-gray-600">Getting covered is simple</p>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {[
              { step: 1, title: 'Tell Us About Yourself', desc: 'Fill out a quick form with your basic info and coverage needs. It takes less than 2 minutes.', icon: FileText },
              { step: 2, title: 'Review Your Options', desc: 'A licensed agent compares plans from multiple carriers to find your best match.', icon: CheckCircle },
              { step: 3, title: 'Get Covered', desc: 'Choose your plan and get started, often within 24-48 hours.', icon: Shield },
            ].map(({ step, title, desc, icon: Icon }, idx) => (
              <AnimatedSection key={step} animation="animate-fade-up" delay={`${idx * 0.2}s`}>
                <div className="text-center relative">
                  <div className="w-16 h-16 bg-brand-blue text-white rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <Icon size={28} />
                  </div>
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-2 w-8 h-8 bg-brand-orange text-white rounded-full flex items-center justify-center text-sm font-bold shadow">
                    {step}
                  </div>
                  <h3 className="text-lg font-bold text-brand-blue mb-2 mt-2">{title}</h3>
                  <p className="text-gray-600">{desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* 5. Final Expense Spotlight */}
      <section className="py-8">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <AnimatedSection animation="animate-slide-left">
              <img
                src="https://static.prod-images.emergentagent.com/jobs/f62ea3e4-bec8-4ef3-9597-f2f1df1dea1e/images/195d6910bdb14cbe32e73495c8f63af27e043f003ea62e7bbb748bce643a7d3c.png"
                alt="Final Expense Insurance"
                className="rounded-xl shadow-lg"
              />
            </AnimatedSection>
            <AnimatedSection animation="animate-slide-right">
              <h2 className="text-3xl md:text-4xl font-bold text-brand-blue mb-6">
                Final Expense Insurance: Our Specialty
              </h2>
              <p className="text-base md:text-lg text-gray-700 mb-6 leading-relaxed">
                Final expense coverage helps protect your loved ones from having to carry the burden of funeral costs, burial expenses, and unpaid final bills. With affordable premiums and simple approval, you can secure peace of mind.
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  { bold: "No Medical Exam for Many Plans", rest: "Simple health questions only" },
                  { bold: "Fixed Premiums", rest: "Your rate never increases" },
                  { bold: "Designed for Ages 50-85", rest: "It's never too late" },
                  { bold: "Simple Quote Process", rest: "Get started in minutes" },
                ].map(item => (
                  <li key={item.bold} className="flex items-start gap-3">
                    <CheckCircle className="text-brand-orange flex-shrink-0 mt-1" size={20} />
                    <span className="text-gray-700"><strong>{item.bold}</strong> - {item.rest}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/final-expense"
                className="inline-flex items-center gap-2 bg-brand-blue text-white px-8 py-3 rounded-md hover:bg-brand-orange transition font-semibold group"
                data-testid="final-expense-link"
              >
                Get My Final Expense Quote <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* 6. Why Choose Us */}
      <section className="py-8 bg-gray-50">
        <div className="container mx-auto px-4">
          <AnimatedSection className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-brand-blue mb-4">Why Choose Purposefully Covered Insurance Group</h2>
            <p className="text-base md:text-lg text-gray-600">Experience, integrity, and personalized service you can trust</p>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              { icon: Shield, title: 'Expert Guidance', desc: 'Licensed agents with years of experience helping families find the right coverage for their unique needs and budget.' },
              { icon: Heart, title: 'Personalized Service', desc: "We take the time to understand your situation and provide tailored recommendations, not one-size-fits-all solutions." },
              { icon: Users, title: 'Family-First Approach', desc: "Your family's financial security is our priority. We serve you with integrity, compassion, and genuine care." },
            ].map(({ icon: Icon, title, desc }, idx) => (
              <AnimatedSection key={title} animation="animate-fade-up" delay={`${idx * 0.15}s`}>
                <div className="text-center hover-lift bg-white rounded-xl p-8 shadow-sm border border-gray-100">
                  <div className="bg-brand-orange text-white w-16 h-16 rounded-2xl flex items-center justify-center mb-4 mx-auto shadow-md">
                    <Icon size={32} />
                  </div>
                  <h3 className="text-lg font-bold text-brand-blue mb-3">{title}</h3>
                  <p className="text-gray-600 text-sm">{desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* 7. Testimonials */}
      <section className="py-8">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-3xl font-bold text-brand-blue">What Our Clients Say</h2>
              <div className="flex gap-2">
                <button onClick={prevTestimonial} className="p-2 rounded-full bg-gray-100 hover:bg-brand-blue hover:text-white transition" data-testid="testimonial-prev">
                  <ChevronLeft size={20} />
                </button>
                <button onClick={nextTestimonial} className="p-2 rounded-full bg-gray-100 hover:bg-brand-blue hover:text-white transition" data-testid="testimonial-next">
                  <ChevronRight size={20} />
                </button>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-8 text-center">
              <img
                src={testimonials[currentTestimonial].image}
                alt={testimonials[currentTestimonial].name}
                className="w-20 h-20 rounded-full mx-auto mb-4 object-cover ring-4 ring-brand-blue/10"
              />
              <div className="flex justify-center mb-3">
                {[...Array(testimonials[currentTestimonial].rating)].map((_, i) => (
                  <Star key={i} size={20} className="text-yellow-400 fill-yellow-400" />
                ))}
              </div>
              <p className="text-gray-700 italic mb-4 text-base leading-relaxed">
                &ldquo;{testimonials[currentTestimonial].text}&rdquo;
              </p>
              <h4 className="font-bold text-brand-blue">{testimonials[currentTestimonial].name}</h4>
              <p className="text-sm text-gray-600">{testimonials[currentTestimonial].role} - {testimonials[currentTestimonial].location}</p>
            </div>
          </div>
        </div>
      </section>

      {/* 8. Quote Form */}
      <section id="quote" className="py-8 relative overflow-hidden scroll-mt-32" data-testid="quote-section">
        <div className="absolute inset-0 bg-gradient-to-br from-brand-blue via-blue-700 to-blue-900"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-2xl mx-auto bg-white rounded-xl shadow-2xl p-8">
            <div className="text-center mb-6">
              <h2 className="text-3xl font-bold text-brand-blue mb-2">Get Your Free Quote</h2>
              <p className="text-gray-600">Fill out the form below and a licensed representative will follow up with you personally.</p>
            </div>
            <QuoteForm />
          </div>
        </div>
      </section>

      {/* 9. FAQ Preview */}
      <section className="py-8 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-brand-blue mb-8">Common Questions</h2>
            <div className="space-y-4 text-left">
              {[
                { q: "Do I need a medical exam to qualify?", a: "Most final expense policies do not require a medical exam. We'll help you find the simplest path to coverage." },
                { q: "How much does coverage cost?", a: "Costs vary based on age, health, and coverage amount. We compare options from multiple carriers to find you the best rate." },
                { q: "How quickly can coverage start?", a: "For many policies, coverage can begin within 24-48 hours after approval." },
              ].map((faq, i) => (
                <div key={i} className="bg-white p-6 rounded-lg shadow-sm">
                  <h3 className="font-bold text-brand-blue mb-2">{faq.q}</h3>
                  <p className="text-gray-600 text-sm">{faq.a}</p>
                </div>
              ))}
            </div>
            <Link to="/faq" className="inline-block mt-6 text-brand-orange hover:text-brand-blue font-semibold" data-testid="faq-link">
              View All FAQs <ArrowRight size={16} className="inline" />
            </Link>
          </div>
        </div>
      </section>

      {/* 10. Final CTA */}
      <section className="py-8 bg-gradient-to-r from-brand-orange to-orange-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <AnimatedSection>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready to Get Covered With Purpose?</h2>
            <p className="text-lg mb-8 max-w-2xl mx-auto">
              Compare coverage options and get a free, no-obligation quote from Purposefully Covered Insurance Group.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="#quote" className="bg-white text-brand-orange px-8 py-4 rounded-md hover:bg-gray-100 transition font-semibold text-lg" data-testid="final-cta-quote">
                Get My Free Quote
              </a>
              <a href={`tel:${companyInfo.phoneRaw}`} className="border-2 border-white text-white px-8 py-4 rounded-md hover:bg-white/10 transition font-semibold text-lg flex items-center justify-center gap-2" data-testid="final-cta-call">
                <Phone size={20} /> Call {companyInfo.phone}
              </a>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* 11. Agent Recruitment CTA */}
      <section className="py-8 bg-brand-blue text-white">
        <div className="container mx-auto px-4 text-center">
          <h3 className="text-2xl font-bold mb-3">Looking to Start a Career in Insurance?</h3>
          <p className="text-base mb-6 max-w-2xl mx-auto text-white/90">
            Join our growing team at Purposefully Covered Insurance Group. Training, mentorship, and growth opportunities available.
          </p>
          <Link
            to="/careers"
            className="inline-flex items-center gap-2 bg-white text-brand-blue px-8 py-3 rounded-md hover:bg-gray-100 transition font-semibold group"
            data-testid="agent-recruitment-link"
          >
            Apply to Join Our Team <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
